import { type HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@services/auth/auth.service';
import { StorageKeys } from '@core/utils/storage-keys.util';
import { catchError, switchMap, throwError, type Observable, share } from 'rxjs';

// Variable to store the refresh token in progress
let refreshTokenInProgress = false;
let refreshTokenSubject: Observable<{ access_token: string }> | null = null;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  const token = localStorage.getItem(StorageKeys.ATO);

  let headers = req.headers;

  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  const authReq = req.clone({ headers });

  if (req.url.includes('/oauth/token')) {
    return next(authReq);
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if ((error.status === 401 || error.status === 403) && !refreshTokenInProgress) {
        refreshTokenInProgress = true;
        refreshTokenSubject = authService.refreshToken().pipe(
          share(), // Important: share the same response between all subscriptions
          catchError(refreshError => {
            refreshTokenInProgress = false;
            refreshTokenSubject = null;
            authService.logout();
            return throwError(() => refreshError);
          })
        );

        return refreshTokenSubject.pipe(
          switchMap(resp => {
            refreshTokenInProgress = false;
            refreshTokenSubject = null;
            const updateHeaders = req.headers.set('Authorization', `Bearer ${resp.access_token}`);
            const newRequest = req.clone({ headers: updateHeaders });
            return next(newRequest);
          })
        );
      } else if ((error.status === 401 || error.status === 403) && refreshTokenSubject) {
        // If there is a refresh in progress, wait for it to finish and retry
        return refreshTokenSubject.pipe(
          switchMap(resp => {
            const updateHeaders = req.headers.set('Authorization', `Bearer ${resp.access_token}`);
            const newRequest = req.clone({ headers: updateHeaders });
            return next(newRequest);
          })
        );
      }

      return throwError(() => error);
    })
  );
};
